/**
 * Image renderer with zoom, rotate, and drag-to-pan support
 */

import { useRef, useState, useCallback, CSSProperties } from 'react';
import { Box, Loader, Stack, Text } from '@mantine/core';
import { IconPhotoOff } from '@tabler/icons-react';

interface ImageRendererProps {
  src: string;
  alt: string;
  zoom: number;
  rotation: number;
}

export function ImageRenderer({ src, alt, zoom, rotation }: ImageRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 100) return;
      const container = containerRef.current;
      if (!container) return;
      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      });
    },
    [zoom]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const container = containerRef.current;
      if (!container) return;
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      container.scrollLeft = dragStart.scrollLeft - dx;
      container.scrollTop = dragStart.scrollTop - dy;
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const containerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    overflow: zoom > 100 ? 'auto' : 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: zoom > 100 ? (isDragging ? 'grabbing' : 'grab') : 'default',
    userSelect: 'none',
  };

  const imgStyle: CSSProperties = {
    maxWidth: zoom <= 100 ? '100%' : undefined,
    maxHeight: zoom <= 100 ? '100%' : undefined,
    width: zoom > 100 ? `${zoom}%` : undefined,
    objectFit: 'contain',
    transform: `rotate(${rotation}deg)`,
    transition: 'transform 0.2s ease',
    pointerEvents: 'none',
  };

  if (hasError) {
    return (
      <Stack align="center" justify="center" h="100%" gap="md">
        <IconPhotoOff size={48} color="var(--mantine-color-gray-5)" />
        <Text c="dimmed" size="sm">Unable to load image</Text>
        <Text c="dimmed" size="xs">{alt}</Text>
      </Stack>
    );
  }

  return (
    <Box
      ref={containerRef}
      style={containerStyle}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {isLoading && (
        <Stack align="center" justify="center" style={{ position: 'absolute', inset: 0 }}>
          <Loader size="md" color="white" />
        </Stack>
      )}
      <img
        src={src}
        alt={alt}
        style={{ ...imgStyle, opacity: isLoading ? 0 : 1 }}
        draggable={false}
        onLoad={() => setIsLoading(false)}
        onError={() => { setIsLoading(false); setHasError(true); }}
      />
    </Box>
  );
}
